import React, { useState, useEffect } from 'react';
import { useSiteData } from '@/context/SiteDataContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PromoPopup = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: ''
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { siteSettings } = useSiteData();

  // Show popup after delay
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsVisible(true);
    }, 5000);
    return () => clearTimeout(timer);
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone is required';
    } else if (!/^[0-9]{10}$/.test(formData.phone.replace(/\s/g, ''))) {
      newErrors.phone = 'Enter a valid 10-digit phone';
    }
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Enter a valid email';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);

    // Submit to backend
    try {
      await fetch(`${API_URL}/api/promo-inquiry`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
    } catch (err) {
      console.error('Error submitting promo inquiry:', err);
    }

    // Create WhatsApp message
    const message = `*New Inquiry - Experience Upgrade*%0A%0A` +
      `*Name:* ${formData.name}%0A` +
      `*Phone:* ${formData.phone}%0A` +
      `*Email:* ${formData.email}%0A%0A` +
      `I'm interested in learning more about your travel profit-sharing program!`;

    const phoneNumber = siteSettings.whatsappNumber.replace('+', '');
    
    // Open WhatsApp
    window.open(`https://wa.me/${phoneNumber}?text=${message}`, '_blank');

    // Reset form
    setTimeout(() => {
      setIsSubmitting(false);
      setShowForm(false);
      setFormData({ name: '', phone: '', email: '' });
    }, 500);
  };

  const handleCTAClick = () => {
    setShowForm(true);
  };

  const handleClose = () => {
    setIsMinimized(true);
  };

  const handleReopen = () => {
    setIsMinimized(false);
  };

  if (!isVisible) return null;

  // Minimized state - show small button to reopen
  if (isMinimized) {
    return (
      <button 
        onClick={handleReopen}
        className="promo-minimized-btn"
        data-testid="promo-reopen-btn"
      >
        <i className="fa-solid fa-gift"></i>
        <span>Special Offer</span>
      </button>
    );
  }

  return (
    <div className="promo-popup-container" data-testid="promo-popup">
      <div className="promo-popup-card">
        {/* Close Button */}
        <button 
          onClick={handleClose}
          className="promo-close-btn"
          data-testid="promo-close-btn"
        >
          <i className="fa-solid fa-xmark"></i>
        </button>

        {!showForm ? (
          // Main Content
          <div className="promo-content">
            <div className="promo-icon-wrapper">
              <i className="fa-solid fa-plane-up"></i>
            </div>
            
            <span className="promo-small-title">Experience Upgrades</span>
            <h3 className="promo-title">Explore, Experience, Empower</h3>
            <p className="promo-text">
              Travel that gives you back. Every journey you take, we share the profits with you — empowering your travel
            </p>
            
            <button 
              onClick={handleCTAClick}
              className="promo-cta-btn"
              data-testid="promo-cta-btn"
            >
              <i className="fa-brands fa-whatsapp"></i>
              <span>Chat with Our Executive</span>
            </button>
          </div>
        ) : (
          // Form Content
          <div className="promo-form-content">
            <button 
              onClick={() => setShowForm(false)}
              className="promo-back-btn"
            >
              <i className="fa-solid fa-arrow-left"></i>
            </button>
            
            <h4 className="promo-form-title">Let's Connect!</h4>
            <p className="promo-form-subtitle">Share your details and our executive will reach out</p>
            
            <form onSubmit={handleSubmit} className="promo-form">
              <div className="promo-form-field">
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Your Name *"
                  className={`promo-input ${errors.name ? 'error' : ''}`}
                  data-testid="promo-name-input"
                />
                {errors.name && <span className="promo-error">{errors.name}</span>}
              </div>
              
              <div className="promo-form-field">
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="Phone Number *"
                  className={`promo-input ${errors.phone ? 'error' : ''}`}
                  data-testid="promo-phone-input"
                />
                {errors.phone && <span className="promo-error">{errors.phone}</span>}
              </div>
              
              <div className="promo-form-field">
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="Email Address *"
                  className={`promo-input ${errors.email ? 'error' : ''}`}
                  data-testid="promo-email-input"
                />
                {errors.email && <span className="promo-error">{errors.email}</span>}
              </div>
              
              <button 
                type="submit"
                className="promo-submit-btn"
                disabled={isSubmitting}
                data-testid="promo-submit-btn"
              >
                {isSubmitting ? (
                  <>
                    <div className="promo-spinner"></div>
                    <span>Connecting...</span>
                  </>
                ) : (
                  <>
                    <i className="fa-brands fa-whatsapp"></i>
                    <span>Connect on WhatsApp</span>
                  </>
                )}
              </button>
            </form>
          </div>
        )}
      </div>
    </div>
  );
};

export default PromoPopup;
